import { useState, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/lib/api.ts'
import {
  Button,
  Card,
  DataTable,
  EmptyState,
  ErrorState,
  FilterBar,
  FilterChip,
  IconButton,
  Input,
  LoadingState,
  Modal,
  PageHeader,
  Spinner,
  useToast,
  type ColumnDef,
} from '@/components/ui'
import {
  Mail,
  MailOpen,
  Inbox,
  Send,
  FileText,
  Folder,
  Paperclip,
  Reply,
  ReplyAll,
  Forward,
  Ticket,
  ChevronLeft,
  Sparkles,
  Download,
  Plus,
  Search,
  RefreshCw,
  CheckCheck,
} from 'lucide-react'
import { formatDistanceToNow, parseISO } from 'date-fns'
import { de } from 'date-fns/locale'

// ─── Types ───────────────────────────────────────────────────────────────────

interface EmailRecipient {
  emailAddress: { name: string; address: string }
}

interface EmailBody {
  contentType: 'html' | 'text'
  content: string
}

interface EmailMessage {
  id: string
  subject: string
  from: EmailRecipient
  toRecipients: EmailRecipient[]
  ccRecipients: EmailRecipient[]
  bccRecipients?: EmailRecipient[]
  receivedDateTime: string
  sentDateTime?: string
  isRead: boolean
  isDraft: boolean
  hasAttachments: boolean
  bodyPreview: string
  body?: EmailBody
  conversationId?: string
  parentFolderId?: string
}

interface MailFolder {
  id: string
  displayName: string
  totalItemCount: number
  unreadItemCount: number
}

interface SharedMailbox {
  id: number
  display_name: string
  email_address: string
  can_read: boolean
  can_send: boolean
}

interface MessageListResult {
  value: EmailMessage[]
  nextLink?: string
}

interface TanssTicketResult {
  id: number
  tanssId?: number
  title?: string
  content?: string
  nr?: string
}

type ComposeMode = 'new' | 'reply' | 'replyAll' | 'forward'

interface ComposeState {
  mode: ComposeMode
  original?: EmailMessage
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function fmtDate(dateStr: string): string {
  try {
    return formatDistanceToNow(parseISO(dateStr), { addSuffix: true, locale: de })
  } catch {
    return dateStr
  }
}

function recipientName(r: EmailRecipient): string {
  return r.emailAddress.name || r.emailAddress.address
}

function recipientList(recipients: EmailRecipient[]): string {
  return recipients.map(recipientName).join(', ')
}

function buildQuote(email: EmailMessage): string {
  return `\n\n---\nVon: ${recipientName(email.from)}\nDatum: ${fmtDate(email.receivedDateTime)}\nBetreff: ${email.subject}\n\n${email.bodyPreview}`
}

// ─── FolderIcon ───────────────────────────────────────────────────────────────

function FolderIcon({ name }: { name: string }) {
  const n = name.toLowerCase()
  if (n.includes('inbox') || n.includes('eingang')) return <Inbox className="h-4 w-4" />
  if (n.includes('sent') || n.includes('gesendet')) return <Send className="h-4 w-4" />
  if (n.includes('draft') || n.includes('entwurf')) return <FileText className="h-4 w-4" />
  return <Folder className="h-4 w-4" />
}

// ─── ComposeModal ─────────────────────────────────────────────────────────────

function ComposeModal({ state, onClose, fromMailbox }: { state: ComposeState | null; onClose: () => void; fromMailbox: string }) {
  const { toast } = useToast()
  const [to, setTo] = useState('')
  const [cc, setCc] = useState('')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [showCc, setShowCc] = useState(false)

  useEffect(() => {
    if (!state) return
    const { mode, original } = state
    if (mode === 'reply' && original) {
      setTo(original.from.emailAddress.address)
      setSubject(`RE: ${original.subject}`)
      setBody(buildQuote(original))
    } else if (mode === 'replyAll' && original) {
      setTo([original.from, ...original.toRecipients].map((r) => r.emailAddress.address).join(', '))
      setCc(original.ccRecipients.map((r) => r.emailAddress.address).join(', '))
      setSubject(`RE: ${original.subject}`)
      setBody(buildQuote(original))
      if (original.ccRecipients.length > 0) setShowCc(true)
    } else if (mode === 'forward' && original) {
      setSubject(`FWD: ${original.subject}`)
      setBody(buildQuote(original))
    } else {
      setTo(''); setCc(''); setSubject(''); setBody('')
    }
  }, [state])

  const sendMutation = useMutation({
    mutationFn: async () => {
      const toArr = to.split(',').map((s) => s.trim()).filter(Boolean)
      const ccArr = cc.split(',').map((s) => s.trim()).filter(Boolean)
      if (state?.mode === 'reply' || state?.mode === 'replyAll') {
        await apiClient.post(`/email/${state.original!.id}/reply`, { body, replyAll: state.mode === 'replyAll' })
      } else if (state?.mode === 'forward') {
        await apiClient.post(`/email/${state.original!.id}/forward`, { to: toArr, body })
      } else {
        await apiClient.post('/email/send', { to: toArr, subject, html: `<div>${body.replace(/\n/g, '<br/>')}</div>`, cc: ccArr.length ? ccArr : undefined })
      }
    },
    onSuccess: () => { toast({ title: 'E-Mail gesendet', tone: 'success' }); onClose() },
    onError: (err: Error) => toast({ title: 'Fehler beim Senden', description: err.message, tone: 'danger' }),
  })

  const title = state?.mode === 'reply' ? 'Antworten' : state?.mode === 'replyAll' ? 'Allen antworten' : state?.mode === 'forward' ? 'Weiterleiten' : 'Neue E-Mail'

  return (
    <Modal
      open={!!state}
      onClose={onClose}
      title={title}
      size="lg"
      footer={
        <div className="flex gap-2 justify-end">
          <Button variant="ghost" onClick={onClose} disabled={sendMutation.isPending}>Abbrechen</Button>
          <Button variant="primary" onClick={() => sendMutation.mutate()} disabled={sendMutation.isPending || (!to && state?.mode === 'new') || !subject}>
            {sendMutation.isPending ? 'Wird gesendet…' : 'Senden'}
          </Button>
        </div>
      }
    >
      <div className="space-y-3">
        {fromMailbox !== 'personal' && <p className="text-xs text-gray-500">Von: {fromMailbox}</p>}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium w-10 shrink-0">An</label>
          <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="empfaenger@beispiel.de" className="flex-1" />
          <button type="button" className="text-xs text-brand-600 hover:underline shrink-0" onClick={() => setShowCc((v) => !v)}>CC</button>
        </div>
        {showCc && (
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium w-10 shrink-0">CC</label>
            <Input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="cc@beispiel.de" className="flex-1" />
          </div>
        )}
        <div className="flex items-center gap-2">
          <label className="text-sm font-medium w-10 shrink-0">Betreff</label>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Betreff" className="flex-1" />
        </div>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Ihre Nachricht…"
          className="w-full min-h-[200px] rounded-md border border-gray-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 resize-y"
        />
      </div>
    </Modal>
  )
}

// ─── TicketAssignModal ────────────────────────────────────────────────────────

function TicketAssignModal({ email, sharedMailbox, onClose }: { email: EmailMessage | null; sharedMailbox?: string; onClose: () => void }) {
  const { toast } = useToast()
  const qc = useQueryClient()
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<{ id: number; title: string } | null>(null)

  const { data: tickets = [], isLoading } = useQuery<TanssTicketResult[]>({
    queryKey: ['tickets', 'search', search],
    queryFn: () => apiClient.get('/tickets', { params: { search, pageSize: '20' } }).then((r) => r.data?.tickets ?? r.data ?? []),
    enabled: search.length >= 2,
  })

  const linkMutation = useMutation({
    mutationFn: () =>
      apiClient.post(`/email/${email!.id}/ticket-link`, { ticketId: selected!.id, direction: 'inbound', matchedBy: 'manual', sharedMailbox }),
    onSuccess: () => {
      toast({ title: 'Ticket verknüpft', description: `Ticket #${selected!.id} zugewiesen`, tone: 'success' })
      qc.invalidateQueries({ queryKey: ['email-links', email!.id] })
      onClose()
    },
    onError: () => toast({ title: 'Fehler', description: 'Verknüpfung fehlgeschlagen', tone: 'danger' }),
  })

  return (
    <Modal open={!!email} onClose={onClose} title="Ticket zuweisen" footer={
      <div className="flex gap-2 justify-end">
        <Button variant="ghost" onClick={onClose}>Abbrechen</Button>
        <Button variant="primary" disabled={!selected || linkMutation.isPending} onClick={() => linkMutation.mutate()}>
          {linkMutation.isPending ? 'Wird gespeichert…' : 'Verknüpfen'}
        </Button>
      </div>
    }>
      <div className="space-y-4">
        <Input value={search} onChange={(e) => { setSearch(e.target.value); setSelected(null) }} placeholder="Ticketnummer oder Betreff…" hint="Mind. 2 Zeichen eingeben" />
        {isLoading && <Spinner size="sm" />}
        {!isLoading && search.length >= 2 && tickets.length === 0 && <p className="text-sm text-gray-500">Keine Tickets gefunden.</p>}
        {tickets.length > 0 && (
          <ul className="divide-y divide-gray-100 rounded-md border border-gray-200 max-h-64 overflow-y-auto">
            {tickets.map((t) => {
              const tid = t.tanssId ?? t.id
              const label = t.title || t.content || `Ticket #${tid}`
              return (
                <li key={t.id} className={`flex items-start gap-3 px-3 py-2 cursor-pointer hover:bg-gray-50 transition-colors ${selected?.id === tid ? 'bg-brand-50 border-l-2 border-brand-500' : ''}`} onClick={() => setSelected({ id: tid, title: label })}>
                  <Ticket className="h-4 w-4 mt-0.5 text-gray-400 shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium">#{tid} – {label}</p>
                  </div>
                </li>
              )
            })}
          </ul>
        )}
        {selected && <div className="rounded-md bg-brand-50 px-3 py-2 text-sm text-brand-700">Ausgewählt: <strong>#{selected.id}</strong> – {selected.title}</div>}
      </div>
    </Modal>
  )
}

// ─── EmailDetail ──────────────────────────────────────────────────────────────

function EmailDetail({ emailId, sharedMailbox: _sharedMailbox, onBack, onReply, onAssignTicket }: {
  emailId: string; sharedMailbox: string;
  onBack: () => void;
  onReply: (mode: 'reply' | 'replyAll' | 'forward', email: EmailMessage) => void;
  onAssignTicket: (email: EmailMessage) => void
}) {
  const { toast } = useToast()
  const qc = useQueryClient()
  const iframeRef = useRef<HTMLIFrameElement>(null)

  const { data: email, isLoading, error } = useQuery<EmailMessage>({
    queryKey: ['email', 'message', emailId],
    queryFn: () => apiClient.get(`/email/${emailId}`).then((r) => r.data),
  })

  const { data: attachments = [] } = useQuery<Array<{ id: string; name: string; contentType: string; size: number; isInline: boolean }>>({
    queryKey: ['email', 'attachments', emailId],
    queryFn: () => apiClient.get(`/email/${emailId}/attachments`, { params: { '$select': 'id,name,contentType,size,isInline' } }).then((r) => r.data?.value ?? []),
    enabled: !!email?.hasAttachments,
  })

  const { data: aiSuggestion } = useQuery<{ status: string; suggestion: string | null }>({
    queryKey: ['email', 'ai-suggestion', emailId],
    queryFn: () => apiClient.get(`/email/${emailId}/ai-suggestion`).then((r) => r.data).catch(() => null),
    enabled: !!email,
    retry: false,
  })

  const markReadMutation = useMutation({
    mutationFn: (isRead: boolean) => apiClient.patch(`/email/${emailId}/read`, { isRead }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['email', 'inbox'] }); qc.invalidateQueries({ queryKey: ['email', 'message', emailId] }) },
    onError: (err: Error) => toast({ title: 'Fehler', description: err.message, tone: 'danger' }),
  })

  useEffect(() => { if (email && !email.isRead) markReadMutation.mutate(true) }, [email?.id, email?.isRead])

  useEffect(() => {
    if (!iframeRef.current || !email?.body) return
    const doc = iframeRef.current.contentDocument
    if (!doc) return
    const content = email.body.contentType === 'html' ? email.body.content : `<pre style="font-family:inherit;white-space:pre-wrap">${email.body.content}</pre>`
    doc.open()
    doc.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><style>body{font-family:system-ui,sans-serif;font-size:14px;line-height:1.6;margin:16px;color:#1f2937}a{color:#2563eb}img{max-width:100%}</style></head><body>${content}</body></html>`)
    doc.close()
  }, [email?.body])

  if (isLoading) return <LoadingState label="E-Mail wird geladen…" />
  if (error || !email) return <ErrorState title="E-Mail konnte nicht geladen werden" description={(error as Error)?.message} onRetry={onBack} retryLabel="Zurück" />

  const visibleAttachments = attachments.filter((a) => !a.isInline)

  return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center gap-2 p-3 border-b border-gray-200 bg-white shrink-0 flex-wrap">
        <IconButton icon={<ChevronLeft />} aria-label="Zurück zur Liste" onClick={onBack} />
        <div className="flex-1" />
        <Button variant="secondary" size="sm" onClick={() => onReply('reply', email)}><Reply className="h-4 w-4 mr-1.5" />Antworten</Button>
        <Button variant="secondary" size="sm" onClick={() => onReply('replyAll', email)}><ReplyAll className="h-4 w-4 mr-1.5" />Allen antworten</Button>
        <Button variant="secondary" size="sm" onClick={() => onReply('forward', email)}><Forward className="h-4 w-4 mr-1.5" />Weiterleiten</Button>
        <Button variant="secondary" size="sm" onClick={() => onAssignTicket(email)}><Ticket className="h-4 w-4 mr-1.5" />Ticket zuweisen</Button>
        <Button variant="ghost" size="sm" onClick={() => markReadMutation.mutate(!email.isRead)} disabled={markReadMutation.isPending}>
          <CheckCheck className="h-4 w-4 mr-1.5" />{email.isRead ? 'Als ungelesen' : 'Als gelesen'}
        </Button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
        <Card>
          <Card.Body>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">{email.subject}</h2>
            <dl className="space-y-1 text-sm">
              <div className="flex gap-2"><dt className="text-gray-500 w-16 shrink-0">Von</dt><dd>{email.from.emailAddress.name} <span className="text-gray-500">&lt;{email.from.emailAddress.address}&gt;</span></dd></div>
              <div className="flex gap-2"><dt className="text-gray-500 w-16 shrink-0">An</dt><dd className="text-gray-700">{recipientList(email.toRecipients)}</dd></div>
              {email.ccRecipients.length > 0 && <div className="flex gap-2"><dt className="text-gray-500 w-16 shrink-0">CC</dt><dd className="text-gray-700">{recipientList(email.ccRecipients)}</dd></div>}
              <div className="flex gap-2"><dt className="text-gray-500 w-16 shrink-0">Datum</dt><dd className="text-gray-700">{fmtDate(email.receivedDateTime)}</dd></div>
            </dl>
          </Card.Body>
        </Card>

        {aiSuggestion && (
          <Card>
            <Card.Body>
              <div className="flex items-center gap-2 mb-2"><Sparkles className="h-4 w-4 text-violet-500" /><span className="text-sm font-medium text-violet-700">KI-Antwortvorschlag</span></div>
              {aiSuggestion.status === 'pending' ? (
                <div className="flex items-center gap-2 text-sm text-gray-500"><Spinner size="sm" /><span>Wird generiert…</span></div>
              ) : (
                <p className="text-sm text-gray-700 whitespace-pre-wrap">{aiSuggestion.suggestion}</p>
              )}
            </Card.Body>
          </Card>
        )}

        <Card>
          <Card.Body className="p-0">
            {email.body ? (
              <iframe ref={iframeRef} title="E-Mail-Inhalt" sandbox="allow-same-origin" className="w-full min-h-[400px] rounded-lg" style={{ border: 'none', display: 'block' }} />
            ) : (
              <div className="p-4 text-sm text-gray-700 whitespace-pre-wrap">{email.bodyPreview}</div>
            )}
          </Card.Body>
        </Card>

        {visibleAttachments.length > 0 && (
          <Card>
            <Card.Header><div className="flex items-center gap-2"><Paperclip className="h-4 w-4 text-gray-500" /><span className="text-sm font-medium">Anhänge ({visibleAttachments.length})</span></div></Card.Header>
            <Card.Body>
              <ul className="space-y-2">
                {visibleAttachments.map((att) => (
                  <li key={att.id} className="flex items-center justify-between gap-3 rounded-md border border-gray-200 px-3 py-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{att.name}</p>
                      <p className="text-xs text-gray-500">{att.contentType} · {Math.round(att.size / 1024)} KB</p>
                    </div>
                    <IconButton icon={<Download />} aria-label={`${att.name} herunterladen`} onClick={() => { const a = document.createElement('a'); a.href = `/api/email/${emailId}/attachments/${att.id}`; a.download = att.name; a.click() }} />
                  </li>
                ))}
              </ul>
            </Card.Body>
          </Card>
        )}
      </div>
    </div>
  )
}

// ─── EmailsPage ───────────────────────────────────────────────────────────────

export function EmailsPage() {
  const [selectedMailbox, setSelectedMailbox] = useState<'personal' | string>('personal')
  const [selectedFolder, setSelectedFolder] = useState<string>('Inbox')
  const [selectedEmailId, setSelectedEmailId] = useState<string | null>(null)
  const [composeState, setComposeState] = useState<ComposeState | null>(null)
  const [ticketEmail, setTicketEmail] = useState<EmailMessage | null>(null)
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [filterIsRead, setFilterIsRead] = useState<'all' | 'read' | 'unread'>('all')
  const [skipTokenStack, setSkipTokenStack] = useState<string[]>([])

  const currentToken = skipTokenStack[skipTokenStack.length - 1]
  const resetPages = () => setSkipTokenStack([])

  const { data: sharedMailboxes = [] } = useQuery<SharedMailbox[]>({
    queryKey: ['email', 'shared-mailboxes'],
    queryFn: () => apiClient.get('/email/shared-mailboxes').then((r) => r.data),
    retry: false,
  })

  const { data: folders = [] } = useQuery<MailFolder[]>({
    queryKey: ['email', 'folders'],
    queryFn: () => apiClient.get('/email/folders').then((r) => r.data),
    retry: false,
  })

  const { data: inbox, isLoading, error, refetch } = useQuery<MessageListResult>({
    queryKey: ['email', 'inbox', selectedMailbox, selectedFolder, search, filterIsRead, currentToken],
    queryFn: () =>
      apiClient.get('/email/inbox', {
        params: {
          folder: selectedFolder,
          sharedMailbox: selectedMailbox !== 'personal' ? selectedMailbox : undefined,
          top: 25,
          search: search || undefined,
          isRead: filterIsRead === 'all' ? undefined : filterIsRead === 'read' ? 'true' : 'false',
          skipToken: currentToken ?? undefined,
        },
      }).then((r) => r.data),
    retry: false,
  })

  const emails = inbox?.value ?? []
  const hasNext = !!inbox?.nextLink

  const sharedMailboxEmail = selectedMailbox !== 'personal' ? selectedMailbox : undefined

  const columns: ColumnDef<EmailMessage>[] = [
    {
      id: 'status',
      header: '',
      size: 32,
      cell: ({ row }) => row.original.isRead ? <MailOpen className="h-4 w-4 text-gray-300" /> : <Mail className="h-4 w-4 text-brand-600" />,
    },
    {
      accessorKey: 'from',
      header: 'Von',
      size: 180,
      cell: ({ row }) => (
        <div className="min-w-0">
          <p className={`truncate text-sm ${!row.original.isRead ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>{recipientName(row.original.from)}</p>
          {row.original.hasAttachments && <Paperclip className="h-3 w-3 text-gray-400 inline mt-0.5" />}
        </div>
      ),
    },
    {
      accessorKey: 'subject',
      header: 'Betreff',
      cell: ({ row }) => (
        <div className="min-w-0">
          <p className={`truncate text-sm ${!row.original.isRead ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>{row.original.subject || '(Kein Betreff)'}</p>
          <p className="truncate text-xs text-gray-400">{row.original.bodyPreview}</p>
        </div>
      ),
    },
    {
      accessorKey: 'receivedDateTime',
      header: 'Datum',
      size: 140,
      cell: ({ row }) => <span className="text-xs text-gray-500 whitespace-nowrap">{fmtDate(row.original.receivedDateTime)}</span>,
    },
  ]

  return (
    <div className="flex flex-col h-full min-h-0">
      <PageHeader
        title="E-Mails"
        breadcrumbs={[{ label: 'Kommunikation' }, { label: 'E-Mails' }]}
        actions={<Button variant="primary" size="sm" onClick={() => setComposeState({ mode: 'new' })}><Plus className="h-4 w-4 mr-1.5" />Neue E-Mail</Button>}
      />

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Sidebar */}
        <aside className="w-56 shrink-0 border-r border-gray-200 bg-white flex flex-col overflow-y-auto">
          <div className="p-3 border-b border-gray-100">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Postfach</p>
            {[{ label: 'Persönlich', value: 'personal' as const }, ...sharedMailboxes.map((mb) => ({ label: mb.display_name, value: mb.email_address }))].map((mb) => (
              <button key={mb.value} onClick={() => { setSelectedMailbox(mb.value); setSelectedFolder('Inbox'); setSelectedEmailId(null); resetPages() }}
                className={`w-full text-left flex items-center gap-2 px-2 py-1.5 rounded-md text-sm transition-colors mt-0.5 ${selectedMailbox === mb.value ? 'bg-brand-50 text-brand-700 font-medium' : 'text-gray-700 hover:bg-gray-50'}`}>
                <Mail className="h-4 w-4 shrink-0" /><span className="truncate">{mb.label}</span>
              </button>
            ))}
          </div>

          <div className="p-3 flex-1">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Ordner</p>
            {folders.length === 0 && <p className="text-xs text-gray-400">Keine Ordner</p>}
            {folders.map((f) => (
              <button key={f.id} onClick={() => { setSelectedFolder(f.id); setSelectedEmailId(null); resetPages() }}
                className={`w-full text-left flex items-center justify-between gap-2 px-2 py-1.5 rounded-md text-sm transition-colors mt-0.5 ${selectedFolder === f.id ? 'bg-brand-50 text-brand-700 font-medium' : 'text-gray-700 hover:bg-gray-50'}`}>
                <div className="flex items-center gap-2 min-w-0"><FolderIcon name={f.displayName} /><span className="truncate">{f.displayName}</span></div>
                {f.unreadItemCount > 0 && <span className="text-xs bg-brand-100 text-brand-700 rounded-full px-1.5 py-0.5 font-medium shrink-0">{f.unreadItemCount}</span>}
              </button>
            ))}
          </div>
        </aside>

        {/* Main */}
        <div className="flex-1 flex flex-col min-w-0 min-h-0">
          {selectedEmailId ? (
            <EmailDetail
              emailId={selectedEmailId}
              sharedMailbox={sharedMailboxEmail ?? ''}
              onBack={() => setSelectedEmailId(null)}
              onReply={(mode, email) => setComposeState({ mode, original: email })}
              onAssignTicket={(email) => setTicketEmail(email)}
            />
          ) : (
            <div className="flex flex-col h-full min-h-0">
              <div className="p-3 border-b border-gray-200 bg-white space-y-2">
                <form onSubmit={(e) => { e.preventDefault(); setSearch(searchInput); resetPages() }} className="flex gap-2">
                  <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Betreff oder Absender suchen…" className="flex-1" />
                  <Button type="submit" variant="secondary" size="sm"><Search className="h-4 w-4" /></Button>
                  <IconButton icon={<RefreshCw />} aria-label="Aktualisieren" onClick={() => refetch()} />
                </form>
                <FilterBar>
                  <FilterChip active={filterIsRead === 'unread'} onClick={() => { setFilterIsRead(filterIsRead === 'unread' ? 'all' : 'unread'); resetPages() }}>Ungelesen</FilterChip>
                  <FilterChip active={filterIsRead === 'read'} onClick={() => { setFilterIsRead(filterIsRead === 'read' ? 'all' : 'read'); resetPages() }}>Gelesen</FilterChip>
                  {search && <FilterChip active onRemove={() => { setSearch(''); setSearchInput(''); resetPages() }}>Suche: „{search}"</FilterChip>}
                </FilterBar>
              </div>

              <div className="flex-1 overflow-auto p-3">
                {isLoading ? (
                  <LoadingState label="E-Mails werden geladen…" />
                ) : error ? (
                  <ErrorState
                    title="Postfach konnte nicht geladen werden"
                    description={(error as { response?: { data?: { error?: string } } })?.response?.data?.error ?? (error as Error).message}
                    onRetry={() => refetch()}
                  />
                ) : emails.length === 0 ? (
                  <EmptyState title="Keine E-Mails" description={search ? 'Keine Treffer.' : 'Dieser Ordner ist leer.'} />
                ) : (
                  <DataTable
                    data={emails}
                    columns={columns}
                    getRowId={(row) => row.id}
                    onRowClick={(row) => setSelectedEmailId(row.id)}
                    rowClassName={(row) => (!row.isRead ? 'bg-blue-50/30' : '')}
                  />
                )}
              </div>

              {(skipTokenStack.length > 0 || hasNext) && (
                <div className="flex items-center justify-between px-3 py-2 border-t border-gray-200 bg-white">
                  <Button variant="ghost" size="sm" disabled={skipTokenStack.length === 0} onClick={() => setSkipTokenStack((p) => p.slice(0, -1))}>← Zurück</Button>
                  <span className="text-sm text-gray-500">Seite {skipTokenStack.length + 1}</span>
                  <Button variant="ghost" size="sm" disabled={!hasNext} onClick={() => {
                    if (!inbox?.nextLink) return
                    try {
                      const url = new URL(inbox.nextLink)
                      const token = url.searchParams.get('$skipToken') || url.searchParams.get('skiptoken') || inbox.nextLink
                      setSkipTokenStack((p) => [...p, token])
                    } catch { setSkipTokenStack((p) => [...p, inbox.nextLink ?? '']) }
                  }}>Weiter →</Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      <ComposeModal state={composeState} onClose={() => setComposeState(null)} fromMailbox={selectedMailbox} />
      <TicketAssignModal email={ticketEmail} sharedMailbox={sharedMailboxEmail} onClose={() => setTicketEmail(null)} />
    </div>
  )
}
